import { CONTRACT_STATUSES, toMillimes } from '@beta-assur/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ContractStatusBadge, InstallmentStatusBadge } from '../components/badges';
import { ConfirmButton } from '../components/confirm';
import { Facts, Field, PageHeader } from '../components/page';
import { ActionError, Empty, QueryView } from '../components/states';
import { Button, buttonVariants } from '../components/ui/button';
import { CoveragesCard, EndorsementsCard } from './contract-coverage';
import { ClaimsCard } from './contract-claims';
import { MandateCard } from './contract-mandate';
import { RenewalCard } from './contract-renewal';
import { download } from './policyholder-rights';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input, Select } from '../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/table';
import { useAuth } from '../lib/auth';
import { day, instant, money } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiQuery } from '../lib/query';
import { useReferentials, useTenant } from '../lib/referentials';
import type { ContractDetail, ContractStatusHistory } from '../lib/types';
import { NoticesCard } from './contract-notices';

// Fiche contrat (BA-023) : données, échéancier, garanties et avenants (BA-069), changements de
// statut historisés.

export function ContractDetailPage() {
  const { t, language } = useI18n();
  const auth = useAuth();
  const { id } = useParams({ strict: false }) as { id: string };
  const refs = useReferentials();
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<ContractDetail>(`/v1/contracts/${id}`);
  const history = useApiQuery<ContractStatusHistory>(`/v1/contracts/${id}/status-history`);
  const [status, setStatus] = useState('');
  const [attestationError, setAttestationError] = useState<Error | null>(null);
  const [reason, setReason] = useState('');

  return (
    <QueryView query={query}>
      {(c) => (
        <>
          <PageHeader
            title={`${t.portfolio.policyNumber} ${c.policyNumber}`}
            subtitle={
              <span className="flex items-center gap-2">
                <ContractStatusBadge status={c.status} />
                <Link
                  to="/assures/$id"
                  params={{ id: c.policyholder.id }}
                  className="text-primary hover:underline"
                >
                  {c.policyholder.displayName}
                </Link>
              </span>
            }
            actions={
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAttestationError(null);
                    download(
                      auth.api,
                      `/v1/contracts/${id}/attestation`,
                      `attestation-${c.policyNumber}.pdf`,
                    ).catch((e: Error) => setAttestationError(e));
                  }}
                >
                  {t.coverage.attestation}
                </Button>
                {auth.can('editPortfolio') ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void download(
                          auth.api,
                          `/v1/contracts/${id}/proof`,
                          `preuve-envoi-${c.policyNumber}.pdf`,
                        )
                      }
                    >
                      {t.rights.proof}
                    </Button>
                    <ConfirmButton
                      label={t.portfolio.changeStatus}
                      title={t.portfolio.changeStatus}
                      message={`${t.portfolio.policyNumber} ${c.policyNumber}`}
                      disabled={false}
                      onConfirm={async () => {
                        await auth.api.request('POST', `/v1/contracts/${id}/status`, {
                          body: { status, reason },
                        });
                        await Promise.all([query.refetch(), history.refetch()]);
                      }}
                    >
                      <Field label={t.portfolio.newStatus}>
                        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                          <option value="">-</option>
                          {CONTRACT_STATUSES.filter((s) => s !== c.status).map((s) => (
                            <option key={s} value={s}>
                              {t.contractStatus[s]}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t.portfolio.reason}>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                      </Field>
                    </ConfirmButton>
                  </>
                ) : null}
              </>
            }
          />
          <div className="flex flex-col gap-4">
            <ActionError error={attestationError} />
            <Card>
              <CardContent>
                <Facts
                  items={[
                    [t.portfolio.insurer, refs.insurerName(c.insurerId)],
                    [t.portfolio.product, refs.productName(c.productLineId)],
                    [
                      t.portfolio.agent,
                      c.agentId ? refs.agentName(c.agentId) : t.portfolio.noAgent,
                    ],
                    [t.portfolio.effectiveDate, day(c.effectiveDate, language)],
                    [t.portfolio.expiryDate, day(c.expiryDate, language)],
                    [t.portfolio.periodicity, t.periodicity[c.periodicity]],
                    [t.portfolio.premium, money(c.premiumAmount, language)],
                    [t.portfolio.insuredObject, c.insuredObjectLabel],
                    [t.portfolio.externalRef, c.externalRef],
                  ]}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t.portfolio.schedule}</CardTitle>
              </CardHeader>
              {c.installments.length === 0 ? (
                <Empty />
              ) : (
                <Table>
                  <THead>
                    <tr>
                      <TH>{t.portfolio.sequenceNo}</TH>
                      <TH>{t.installments.dueDate}</TH>
                      <TH>{t.installments.period}</TH>
                      <TH className="text-end">{t.installments.amountDue}</TH>
                      <TH className="text-end">{t.installments.amountPaid}</TH>
                      <TH>{t.installments.status}</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {c.installments.map((i) => (
                      <TR key={i.id}>
                        <TD>{i.sequenceNo}</TD>
                        <TD>
                          <Link
                            to="/echeances/$id"
                            params={{ id: i.id }}
                            className="text-primary hover:underline"
                          >
                            {day(i.dueDate, language)}
                          </Link>
                        </TD>
                        <TD className="whitespace-nowrap">
                          {day(i.periodStart, language)} - {day(i.periodEnd, language)}
                        </TD>
                        <TD className="text-end tabular-nums">{money(i.amountDue, language)}</TD>
                        <TD className="text-end tabular-nums">{money(i.amountPaid, language)}</TD>
                        <TD>
                          <InstallmentStatusBadge status={i.status} />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </Card>
            <NoticesCard
              contractId={c.id}
              dueNoticeDays={c.dueNoticeDays}
              renewalNoticeDays={c.renewalNoticeDays}
              editable={auth.can('editPortfolio')}
            />
            <CoveragesCard contractId={c.id} editable={auth.can('editPortfolio')} />
            <MandateCard contractId={c.id} editable={auth.can('editPortfolio')} />
            <ClaimsCard
              contractId={c.id}
              canDeclare={auth.can('recordPayment')}
              canFollow={auth.can('editPortfolio')}
            />
            {c.expiryDate ? (
              <RenewalCard
                contractId={c.id}
                premium={toMillimes(c.premiumAmount)}
                expiryDate={c.expiryDate}
                editable={auth.can('editPortfolio') && c.status === 'active'}
              />
            ) : null}
            <EndorsementsCard
              contractId={c.id}
              editable={auth.can('editPortfolio') && c.status === 'active'}
            />
            <Card>
              <CardHeader>
                <CardTitle>{t.portfolio.statusHistory}</CardTitle>
              </CardHeader>
              {!history.data || history.data.items.length === 0 ? (
                <Empty />
              ) : (
                <ul className="divide-y divide-border">
                  {history.data.items.map((h) => (
                    <li
                      key={h.id}
                      className="flex flex-wrap justify-between gap-2 px-4 py-2 text-sm"
                    >
                      <span>
                        {h.fromStatus ? `${t.contractStatus[h.fromStatus]} > ` : ''}
                        {t.contractStatus[h.toStatus]}
                        {h.reason ? (
                          <span className="text-muted-foreground"> ({h.reason})</span>
                        ) : null}
                      </span>
                      <span className="text-muted-foreground">
                        {instant(h.changedAt, tz, language)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <div>
              <Link
                to="/echeances"
                search={{ policyNumber: c.policyNumber }}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                {t.installments.title}
              </Link>
            </div>
          </div>
        </>
      )}
    </QueryView>
  );
}
